import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Camera,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileImage,
  Sparkles,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  'Fournitures', 'Transport', 'Restauration', 'Hébergement',
  'Prestations', 'Télécoms', 'Assurance', 'Formation',
  'Marketing', 'Loyer', 'Matériel', 'Autres',
];

const TVA_RATES = [
  { value: 20, label: '20% (standard)' },
  { value: 10, label: '10% (restauration, transport)' },
  { value: 5.5, label: '5,5% (produits alimentaires, culturels)' },
  { value: 2.1, label: '2,1% (médicaments remboursables)' },
  { value: 0, label: '0% (franchise / hors UE / non applicable)' },
];

const ScanFacturePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // 'upload' | 'analyzing' | 'review'
  const [preview, setPreview] = useState(null); // dataURL for images, filename for PDF
  const [previewIsPdf, setPreviewIsPdf] = useState(false);
  const [attachmentId, setAttachmentId] = useState(null);
  const [filename, setFilename] = useState('');
  const [confiance, setConfiance] = useState(null);

  // Form fields (pre-filled by LLM, editable)
  const [fournisseur, setFournisseur] = useState('');
  const [dateFacture, setDateFacture] = useState('');
  const [numeroFacture, setNumeroFacture] = useState('');
  const [montantHt, setMontantHt] = useState('');
  const [tauxTva, setTauxTva] = useState(20);
  const [montantTva, setMontantTva] = useState('');
  const [montantTtc, setMontantTtc] = useState('');
  const [categorie, setCategorie] = useState('Fournitures');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const isTvaSubject = user?.is_tva_subject !== false;

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Client-side validation
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, WEBP ou PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop lourd (max 10 Mo).');
      return;
    }

    // Preview
    setFilename(file.name);
    setPreviewIsPdf(file.type === 'application/pdf');
    if (file.type === 'application/pdf') {
      setPreview(file.name);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    // Upload + OCR
    setStep('analyzing');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API_URL}/scan-facture`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 60s pour laisser le temps au LLM
      });

      const e = data.extracted || {};
      setAttachmentId(data.attachment_id);
      setFournisseur(e.fournisseur || '');
      setDateFacture(e.date_facture || new Date().toISOString().split('T')[0]);
      setNumeroFacture(e.numero_facture || '');
      setMontantHt(e.montant_ht != null ? String(e.montant_ht) : '');
      setTauxTva(e.taux_tva != null ? Number(e.taux_tva) : (isTvaSubject ? 20 : 0));
      setMontantTva(e.montant_tva != null ? String(e.montant_tva) : '');
      setMontantTtc(e.montant_ttc != null ? String(e.montant_ttc) : '');
      setCategorie(e.categorie_suggeree || 'Fournitures');
      setDescription(e.fournisseur ? `Facture ${e.fournisseur}` : 'Facture fournisseur');
      setConfiance(e.confiance || 'moyenne');
      setStep('review');
      toast.success('Facture analysée ✨');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur analyse';
      toast.error(detail);
      setStep('upload');
      setPreview(null);
    }
  };

  const handleRecalcTtc = () => {
    // Auto-calcul TTC quand HT + taux TVA changent
    const ht = parseFloat(montantHt);
    if (!isNaN(ht) && !isNaN(tauxTva)) {
      const tva = ht * (tauxTva / 100);
      const ttc = ht + tva;
      setMontantTva(tva.toFixed(2));
      setMontantTtc(ttc.toFixed(2));
    }
  };

  const handleSave = async () => {
    if (!montantTtc || parseFloat(montantTtc) <= 0) {
      toast.error('Le montant TTC est obligatoire');
      return;
    }
    if (!categorie) {
      toast.error('Choisissez une catégorie');
      return;
    }

    setSaving(true);
    try {
      // POST /api/depenses avec attachment_id lié
      await axios.post(
        `${API_URL}/depenses`,
        {
          description: description || `Facture ${fournisseur || 'fournisseur'}`,
          montant_ttc: parseFloat(montantTtc),
          tva_rate: isTvaSubject ? parseFloat(tauxTva) : 0,
          categorie,
          date: dateFacture ? new Date(dateFacture).toISOString() : new Date().toISOString(),
          fournisseur: fournisseur || null,
          statut: 'payee',
          condition_paiement: 'comptant',
          attachment_id: attachmentId,
        },
        { withCredentials: true }
      );
      toast.success('Dépense enregistrée avec la facture jointe ✅');
      navigate('/depenses');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    setStep('upload');
    setPreview(null);
    setAttachmentId(null);
    setFournisseur(''); setDateFacture(''); setNumeroFacture('');
    setMontantHt(''); setTauxTva(20); setMontantTva(''); setMontantTtc('');
    setCategorie('Fournitures'); setDescription(''); setConfiance(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="scan-facture-page">
      <button
        onClick={() => navigate('/depenses')}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        data-testid="back-to-depenses"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour aux dépenses
      </button>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-emerald-600" />
          Scanner une facture fournisseur
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Prenez une photo ou importez un PDF. L&apos;IA extrait automatiquement les champs et
          {isTvaSubject
            ? ' calcule votre TVA déductible.'
            : ' enregistre le TTC (vous êtes en franchise TVA, aucune TVA à déduire).'}
        </p>
      </div>

      {/* ETAPE 1 : UPLOAD */}
      {step === 'upload' && (
        <Card className="border-dashed border-2 border-slate-300 hover:border-emerald-400 transition-colors" data-testid="upload-zone">
          <CardContent className="p-8 sm:p-12">
            <div className="flex flex-col items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
                <Camera className="h-10 w-10 text-emerald-600" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-lg">Choisissez comment importer</p>
                <p className="text-sm text-muted-foreground mt-1">
                  JPG, PNG, WEBP ou PDF — 10 Mo max
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-md">
                <Button
                  size="lg"
                  onClick={() => cameraInputRef.current?.click()}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                  data-testid="btn-take-photo"
                >
                  <Camera className="h-5 w-5" />
                  Prendre une photo
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                  data-testid="btn-upload-file"
                >
                  <Upload className="h-5 w-5" />
                  Importer un fichier
                </Button>
              </div>

              {/* Inputs cachés */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                data-testid="camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                data-testid="file-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ETAPE 2 : ANALYSE EN COURS */}
      {step === 'analyzing' && (
        <Card data-testid="analyzing-card">
          <CardContent className="p-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-emerald-600 animate-spin" />
              <p className="font-semibold text-lg">Analyse en cours…</p>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Claude Sonnet 4.6 lit votre facture, identifie le fournisseur, la date,
                les montants HT/TVA/TTC et suggère une catégorie.
              </p>
              <div className="text-xs text-muted-foreground">Cela prend 5 à 15 secondes.</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ETAPE 3 : VERIFICATION + VALIDATION */}
      {step === 'review' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="review-form">
          {/* Preview (image) */}
          <Card className="lg:col-span-2 self-start">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileImage className="h-4 w-4" />
                Facture scannée
              </CardTitle>
              <CardDescription className="text-xs truncate">{filename}</CardDescription>
            </CardHeader>
            <CardContent>
              {previewIsPdf ? (
                <div className="aspect-[3/4] bg-slate-100 rounded flex items-center justify-center text-slate-500">
                  📄 Aperçu PDF non disponible
                </div>
              ) : (
                <img
                  src={preview}
                  alt="Facture"
                  className="w-full rounded border max-h-[500px] object-contain bg-slate-50"
                  data-testid="scan-preview-img"
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={resetAll}
                className="w-full mt-3 gap-2"
                data-testid="btn-scan-another"
              >
                <RefreshCw className="h-4 w-4" />
                Scanner une autre facture
              </Button>
            </CardContent>
          </Card>

          {/* Form fields */}
          <Card className="lg:col-span-3">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    Vérifiez et validez
                  </CardTitle>
                  <CardDescription>Corrigez si besoin, puis enregistrez</CardDescription>
                </div>
                {confiance && (
                  <div
                    className={`px-2 py-1 rounded text-xs font-semibold ${
                      confiance === 'haute'
                        ? 'bg-emerald-100 text-emerald-800'
                        : confiance === 'moyenne'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                    data-testid="confiance-badge"
                  >
                    {confiance === 'haute' && '✓ Haute confiance'}
                    {confiance === 'moyenne' && '⚠ Vérifiez les montants'}
                    {confiance === 'faible' && '⚠ Faible qualité — vérifiez tout'}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fournisseur">Fournisseur *</Label>
                  <Input
                    id="fournisseur"
                    value={fournisseur}
                    onChange={(e) => setFournisseur(e.target.value)}
                    placeholder="Nom du fournisseur"
                    data-testid="input-fournisseur"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date de facture *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={dateFacture}
                    onChange={(e) => setDateFacture(e.target.value)}
                    data-testid="input-date"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="numero">N° de facture</Label>
                <Input
                  id="numero"
                  value={numeroFacture}
                  onChange={(e) => setNumeroFacture(e.target.value)}
                  placeholder="Ex : F2026-001"
                  data-testid="input-numero"
                />
              </div>

              {/* Ventilation TVA selon statut */}
              {isTvaSubject ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                    <strong>💡 Vous êtes assujettie à la TVA</strong> — le montant HT et la TVA sont séparés
                    (TVA déductible en fin de trimestre).
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="ht">Montant HT (€) *</Label>
                      <Input
                        id="ht"
                        type="number"
                        step="0.01"
                        value={montantHt}
                        onChange={(e) => setMontantHt(e.target.value)}
                        onBlur={handleRecalcTtc}
                        data-testid="input-ht"
                      />
                    </div>
                    <div>
                      <Label htmlFor="taux">Taux TVA *</Label>
                      <Select
                        value={String(tauxTva)}
                        onValueChange={(v) => { setTauxTva(Number(v)); }}
                      >
                        <SelectTrigger id="taux" data-testid="select-taux-tva">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TVA_RATES.map((r) => (
                            <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="tva">Montant TVA (€)</Label>
                      <Input
                        id="tva"
                        type="number"
                        step="0.01"
                        value={montantTva}
                        onChange={(e) => setMontantTva(e.target.value)}
                        data-testid="input-tva"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ttc">Montant TTC (€) *</Label>
                    <Input
                      id="ttc"
                      type="number"
                      step="0.01"
                      value={montantTtc}
                      onChange={(e) => setMontantTtc(e.target.value)}
                      className="font-semibold text-lg"
                      data-testid="input-ttc"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
                    <strong>💡 Vous êtes en franchise en base de TVA</strong> — vous n&apos;avez pas de TVA à déduire.
                    Seul le montant TTC est enregistré comme dépense.
                  </div>
                  <div>
                    <Label htmlFor="ttc-simple">Montant TTC (€) *</Label>
                    <Input
                      id="ttc-simple"
                      type="number"
                      step="0.01"
                      value={montantTtc}
                      onChange={(e) => setMontantTtc(e.target.value)}
                      className="font-semibold text-lg"
                      data-testid="input-ttc"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="cat">Catégorie *</Label>
                <Select value={categorie} onValueChange={setCategorie}>
                  <SelectTrigger id="cat" data-testid="select-categorie">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="desc">Description</Label>
                <Input
                  id="desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex : Achat matériel bureau"
                  data-testid="input-description"
                />
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-2 rounded text-xs text-amber-900 flex gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Vérifiez toujours les montants extraits — l&apos;IA peut se tromper sur des factures peu lisibles.</span>
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                size="lg"
                data-testid="btn-save-depense"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement…</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Enregistrer la dépense</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ScanFacturePage;
